import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { StaffThemeProvider } from './shared/theme/staff-theme-context';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import './main.css';

/**
 * В Docker web `config.js` задаёт `window.DOMCRM_MOBILE_DOMAIN` до бандла.
 * Локально Vite: `VITE_DOMCRM_MOBILE_DOMAIN` из `.env.development` / `.env.local`, иначе в dev — **`http://127.0.0.1:5182`**
 * (тот же порт, что `npm run dev` в `frontend/embed`, base `/embed/`). Для Docker-widgets: `http://127.0.0.1:20103` в `.env.local`.
 */
function ensureStaffMobileDomainRuntime(): void {
  const w = window as Window & { DOMCRM_MOBILE_DOMAIN?: string };
  if (String(w.DOMCRM_MOBILE_DOMAIN ?? '').trim() !== '') return;
  const fromVite = import.meta.env.VITE_DOMCRM_MOBILE_DOMAIN;
  if (typeof fromVite === 'string' && fromVite.trim() !== '') {
    w.DOMCRM_MOBILE_DOMAIN = fromVite.trim();
    return;
  }
  if (import.meta.env.DEV) {
    /** Согласовано с `frontend/embed` dev (порт 5182, base `/embed/`) — LAB/iframe без контейнера widgets. */
    w.DOMCRM_MOBILE_DOMAIN = 'http://127.0.0.1:5182';
  }
}

ensureStaffMobileDomainRuntime();

/**
 * Авто-восстановление после деплоя: если динамический чанк не загрузился (старый index
 * или сетевой сбой), Vite эмитит `vite:preloadError`. Один раз перезагружаем страницу,
 * чтобы получить актуальные index и чанки. Анти-петля: не чаще раза в 10 секунд.
 */
const CHUNK_RELOAD_TS = 'domcrm-staff-chunk-reload-ts';
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(CHUNK_RELOAD_TS) ?? '0');
  } catch {
    /* sessionStorage может быть недоступен */
  }
  const now = Date.now();
  if (now - last < 10_000) return;
  try {
    sessionStorage.setItem(CHUNK_RELOAD_TS, String(now));
  } catch {
    /* ignore */
  }
  window.location.reload();
});

const el = document.getElementById('root');
if (!el) throw new Error('root not found');

createRoot(el).render(
  <StrictMode>
    <StaffThemeProvider>
      <App />
    </StaffThemeProvider>
  </StrictMode>
);
